/**
 * Client feedback (2026-09) — short, human-readable labels for notification types.
 *
 * The bell dropdown and the alert detail dialog previously showed the raw enum
 * name (`LogsheetRejectedToChecker`), which reads as a code identifier rather
 * than information. The backend's `NotificationTypeDescriptions` strings are
 * written for the admin subscription matrix and are full sentences — too long
 * for a row label — so the compact labels live here.
 *
 * Keep in sync with `NotificationTypeValue` in `@/types/notifications`.
 */

import type { NotificationTypeValue } from '@/types/notifications';

const LABELS: Readonly<Record<NotificationTypeValue, string>> = {
  LogsheetSubmitted: 'Logsheet submitted',
  LogsheetRejectedToMaker: 'Rejected to Maker',
  LogsheetRejectedToChecker: 'Rejected to Checker',
  LogsheetCheckedToApprover: 'Awaiting approval',
  LogsheetApprovedToMaker: 'Logsheet approved',
  LogsheetApprovedToApprover: 'Logsheet approved',
  MonthlySummaryReady: 'Monthly summary ready',
  DailyReport: 'Daily report',
};

/**
 * Returns a short label for a notification type. Falls back to the raw value so a
 * type added on the backend before the frontend catches up still renders something
 * rather than an empty cell — hence the lookup through a widened record rather than
 * a direct index, which TypeScript would prove exhaustive and drop the fallback.
 */
export function notificationTypeLabel(type: NotificationTypeValue): string {
  const label: string | undefined = (LABELS as Record<string, string | undefined>)[type];
  return label ?? type;
}
